import type {
  CountryIntelligenceRulesAllowances,
  CountryIntelligenceRulesTimeOff,
  CountryIntelligenceRulesResignation,
  CountryIntelligenceRulesTermination,
  CountryIntelligenceRulesProbation,
} from "@oysterhr/schema"
import {
  CountryIntelligenceOysterScore,
  CountryIntelligenceRulesTimeOffType,
  CalendarDurationType,
  CountryIntelligenceEmploymentType,
  CountryIntelligenceTeamMemberLevel,
  CountryIntelligenceRulesAllowancesPaidWhen,
  CountryIntelligenceRulesAllowancesType,
  PostTerminationPaymentAmountType,
  PostTerminationPaymentFrequency,
  PostTerminationRestrictedArea,
  BenefitTier,
} from "@oysterhr/schema"
import * as bonusesAndAllowances from "../../pages/variableCompensation/bonus"
import * as timeOff from "../../pages/timeOff"
import * as probationPeriod from "../../pages/probationPeriod"
import * as countryHighlights from "../../pages/countryHighlights"
import * as restrictiveCovenantsPaymentType from "../../pages/restrictiveCovenants/paymentType"
import * as benefitsTiers from "../../pages/benefits/tier"
import type { GetComponentProps } from "../../types"
import { getGQLFragmentName } from "../useKBIntegration/fetchCountryIntelligence"
import type { MockedResponse } from "@apollo/client/testing"
import { GET_SUBDIVISIONS } from "~/app/queries/useSubdivisions"

/**
 *
 * !! IMPORTANT !!
 * This mock is setup to only work if you select Tokelau (TK) as a country
 * !! IMPORTANT !!
 *
 * The intention behind it is to allow testing for country specific data
 * while also allowing testing without KB when the mock flag is enabled
 *
 */

export const fetchCountryIntelligenceMock = ({
  queryFragmentName,
}: {
  queryFragmentName: string
}): Promise<ReturnType<typeof getMockData>> => {
  console.log("Using CountryIntelligenceMock...")

  return new Promise(resolve => {
    setTimeout(() => {
      resolve(getMockData(queryFragmentName))
    }, 500)
  })
}

export const getMockData = (queryFragmentName: string) => {
  switch (queryFragmentName) {
    case getGQLFragmentName(bonusesAndAllowances.queryFragment):
      return bonusMock
    case getGQLFragmentName(timeOff.queryFragment):
      return timeOffMock
    case getGQLFragmentName(probationPeriod.queryFragment):
      return probationPeriodMock
    case getGQLFragmentName(countryHighlights.queryFragment):
      return countryHighlightsMock
    case getGQLFragmentName(restrictiveCovenantsPaymentType.queryFragment):
      return restrictiveCovenantsPaymentTypeMock
    case getGQLFragmentName(benefitsTiers.queryFragment):
      return benefitsTierMock
    default:
      return undefined
  }
}

const probationPeriodList: CountryIntelligenceRulesProbation[] = [
  {
    oysterScore: CountryIntelligenceOysterScore.Statutory,
    duration: {
      value: 2,
      unit: CalendarDurationType.Months,
    },
    employmentType: CountryIntelligenceEmploymentType.Permanent,
    teamMemberLevel: CountryIntelligenceTeamMemberLevel.Executive,
    extension: {
      deadline: {
        value: 10,
        unit: CalendarDurationType.Days,
      },
      maxAdditionalDuration: {
        value: 15,
        unit: CalendarDurationType.Weeks,
      },
    },
    tenureRange: {
      min: {
        value: 1,
        unit: CalendarDurationType.Years,
      },
      max: {
        value: 2,
        unit: CalendarDurationType.Years,
      },
    },
  },
  {
    oysterScore: CountryIntelligenceOysterScore.Statutory,
    duration: {
      value: 6,
      unit: CalendarDurationType.Months,
    },
    employmentType: CountryIntelligenceEmploymentType.Temporary,
    tenureRange: {
      min: {
        value: 1,
        unit: CalendarDurationType.Years,
      },
      // @todo: typing here is wrong, this is not required
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      max: null as any,
    },
  },
  {
    oysterScore: CountryIntelligenceOysterScore.Statutory,
    duration: {
      value: 10,
      unit: CalendarDurationType.Months,
    },
    teamMemberLevel: CountryIntelligenceTeamMemberLevel.NonExecutive,
    employmentType: CountryIntelligenceEmploymentType.Permanent,
  },
  {
    oysterScore: CountryIntelligenceOysterScore.Recommended,
    duration: {
      value: 2,
      unit: CalendarDurationType.Months,
    },
    employmentType: CountryIntelligenceEmploymentType.Permanent,
  },
  {
    oysterScore: CountryIntelligenceOysterScore.Recommended,
    duration: {
      value: 6,
      unit: CalendarDurationType.Weeks,
    },
    teamMemberLevel: CountryIntelligenceTeamMemberLevel.NonExecutive,
    employmentType: CountryIntelligenceEmploymentType.Temporary,
  },
  {
    oysterScore: CountryIntelligenceOysterScore.Customary,
    duration: {
      value: 2,
      unit: CalendarDurationType.Months,
    },
    employmentType: CountryIntelligenceEmploymentType.Permanent,
  },
  {
    oysterScore: CountryIntelligenceOysterScore.Customary,
    duration: {
      value: 6,
      unit: CalendarDurationType.Weeks,
    },
    teamMemberLevel: CountryIntelligenceTeamMemberLevel.NonExecutive,
    employmentType: CountryIntelligenceEmploymentType.Temporary,
  },
]

const timeOffList: CountryIntelligenceRulesTimeOff[] = [
  {
    amountPerYear: {
      unit: CalendarDurationType.Days,
      value: 30,
    },
    oysterScore: CountryIntelligenceOysterScore.Statutory,
    type: CountryIntelligenceRulesTimeOffType.Vacations,
  },
  {
    amountPerYear: {
      unit: CalendarDurationType.Days,
      value: 90,
    },
    oysterScore: CountryIntelligenceOysterScore.TopOfMarket,
    type: CountryIntelligenceRulesTimeOffType.Vacations,
  },
  {
    amountPerYear: {
      unit: CalendarDurationType.Days,
      value: 100,
    },
    oysterScore: CountryIntelligenceOysterScore.Customary,
    type: CountryIntelligenceRulesTimeOffType.Vacations,
  },
  {
    amountPerYear: {
      unit: CalendarDurationType.Days,
      value: 100,
    },
    oysterScore: CountryIntelligenceOysterScore.Recommended,
    type: CountryIntelligenceRulesTimeOffType.Vacations,
  },
]

const allowancesList: CountryIntelligenceRulesAllowances[] = [
  {
    type: CountryIntelligenceRulesAllowancesType.WfhAllowance,
    approximation: true,
    value: ["US$", "100"],
    paidWhen: CountryIntelligenceRulesAllowancesPaidWhen.Monthly,
    partOfGrossSalary: false,
    oysterScore: CountryIntelligenceOysterScore.Statutory,
  },
  {
    type: CountryIntelligenceRulesAllowancesType.VacationBonus,
    approximation: true,
    value: ["US$", "1,000"],
    paidWhen: CountryIntelligenceRulesAllowancesPaidWhen.June,
    partOfGrossSalary: true,
    oysterScore: CountryIntelligenceOysterScore.Statutory,
  },
  {
    type: CountryIntelligenceRulesAllowancesType.PerformanceBonus,
    approximation: false,
    value: ["US$", "500"],
    paidWhen: CountryIntelligenceRulesAllowancesPaidWhen.Anytime,
    partOfGrossSalary: false,
    oysterScore: CountryIntelligenceOysterScore.Statutory,
  },
  {
    type: CountryIntelligenceRulesAllowancesType.DepositSaving,
    approximation: false,
    value: undefined,
    paidWhen: CountryIntelligenceRulesAllowancesPaidWhen.Anytime,
    partOfGrossSalary: false,
    oysterScore: CountryIntelligenceOysterScore.Recommended,
  },
]

export const bonusMock: GetComponentProps<
  typeof bonusesAndAllowances.TranslationComponent
>["data"] = {
  isoAlpha2Code: "TK",
  partOfGrossSalary: allowancesList.filter(allowance => allowance.partOfGrossSalary),
  notPartOfGrossSalary: allowancesList.filter(allowance => !allowance.partOfGrossSalary),
}

export const timeOffMock: GetComponentProps<typeof timeOff.TranslationComponent>["data"] = {
  name: "Tokelau",
  isoAlpha2Code: "TK",
  nationalHolidaysPerYear: 10,
  statutoryTimeOff: timeOffList.filter(
    t => t.oysterScore === CountryIntelligenceOysterScore.Statutory,
  ),
  customaryTimeOff: timeOffList.filter(
    t => t.oysterScore === CountryIntelligenceOysterScore.Customary,
  ),
  recommendedTimeOff: timeOffList.filter(
    t => t.oysterScore === CountryIntelligenceOysterScore.Recommended,
  ),
  topOfMarketTimeOff: timeOffList.filter(
    t => t.oysterScore === CountryIntelligenceOysterScore.TopOfMarket,
  ),
}

export const probationPeriodMock: GetComponentProps<
  typeof probationPeriod.TranslationComponent
>["data"] = {
  isoAlpha2Code: "TK",
  statutory: probationPeriodList?.filter(
    p => p.oysterScore === CountryIntelligenceOysterScore.Statutory,
  ),
  recommended: probationPeriodList?.filter(
    p => p.oysterScore === CountryIntelligenceOysterScore.Recommended,
  ),
  customary: probationPeriodList?.filter(
    p => p.oysterScore === CountryIntelligenceOysterScore.Customary,
  ),
}

export const countryHighlightsMock: GetComponentProps<
  typeof countryHighlights.TranslationComponent
>["data"] = {
  name: "Tokelau",
  flagUtf8: "🇹🇰",
  isoAlpha2Code: "TK",
  nationalHolidaysPerYear: 10,

  statutoryProbationPeriod: probationPeriodList.filter(
    period => period.oysterScore === CountryIntelligenceOysterScore.Statutory,
  ),
  recommendedProbationPeriod: probationPeriodList.filter(
    period => period.oysterScore === CountryIntelligenceOysterScore.Statutory,
  ),

  timeOffRules: timeOffList,
  resignationRules: [
    {
      notice: {
        value: 1,
        unit: CalendarDurationType.Months,
      },
    },
  ] as CountryIntelligenceRulesResignation[],
  terminationRules: [
    {
      notice: {
        value: 2,
        unit: CalendarDurationType.Weeks,
      },
    },
  ] as CountryIntelligenceRulesTermination[],
}

export const restrictiveCovenantsPaymentTypeMock: GetComponentProps<
  typeof restrictiveCovenantsPaymentType.TranslationComponent
>["data"] = {
  isoAlpha2Code: "TK",
  restrictiveCovenants: {
    __typename: "CountryIntelligenceRulesRestrictiveCovenants",
    postTermination: {
      __typename: "CountryIntelligenceRulesRestrictiveCovenantsPostTermination",
      paymentRequired: true,
      maxDuration: {
        __typename: "CalendarDuration",
        unit: CalendarDurationType.Months,
        value: 6,
      },
      suggestedDurations: [
        {
          __typename: "CalendarDuration",
          unit: CalendarDurationType.Months,
          value: 6,
        },
      ],
      paymentRules: [
        {
          __typename: "PostTerminationPaymentRule",
          amountType: PostTerminationPaymentAmountType.Flat,
          frequencyRules: [
            {
              __typename: "PostTerminationPaymentFrequencyRule",
              frequency: PostTerminationPaymentFrequency.OnTermination,
              restrictedAreaRules: [
                {
                  __typename: "PostTerminationRestrictedAreaPaymentRule",
                  additionalAreasAllowed: false,
                  restrictedArea: PostTerminationRestrictedArea.World,
                },
                {
                  __typename: "PostTerminationRestrictedAreaPaymentRule",
                  additionalAreasAllowed: true,
                  restrictedArea: PostTerminationRestrictedArea.Country,
                },
              ],
            },
            {
              __typename: "PostTerminationPaymentFrequencyRule",
              frequency: PostTerminationPaymentFrequency.MonthlyPostTermination,
              restrictedAreaRules: [
                {
                  __typename: "PostTerminationRestrictedAreaPaymentRule",
                  additionalAreasAllowed: false,
                  restrictedArea: PostTerminationRestrictedArea.City,
                },
                {
                  __typename: "PostTerminationRestrictedAreaPaymentRule",
                  additionalAreasAllowed: false,
                  restrictedArea: PostTerminationRestrictedArea.AdministrativeDivision,
                },
              ],
            },
          ],
        },
      ],
    },
  },
}

export const benefitsTierMock: GetComponentProps<
  typeof benefitsTiers.TranslationComponent
>["data"] = {
  isoAlpha2Code: "TK",
  countryBenefits: {
    collection: [
      {
        benefit: "Public health insurance (Sécurité Sociale)",
        countryCode: "TK",
        tier: BenefitTier.Statutory,
      },
      {
        benefit: "Unemployment insurance",
        countryCode: "TK",
        tier: BenefitTier.Statutory,
      },
    ],
  },
}

export const subdivisionsMock: MockedResponse[] = [
  {
    request: {
      query: GET_SUBDIVISIONS,
      variables: { isoAlpha2Code: "US" },
    },
    result: {
      data: {
        country: {
          isoLongName: "The United States of America",
          isoShortName: "United States of America",
          alpha2: "US",
          subdivisions: [
            {
              name: "California",
              code: "CA",
              type: "state",
            },
            {
              name: "Not a state",
              code: "XY",
              type: "region",
            },
            {
              name: "Hawaii",
              code: "HI",
              type: "state",
            },
            {
              name: "Maine",
              code: "ME",
              type: "state",
            },
          ],
        },
      },
    },
  },
]
